import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Currency {
  id: number;
  code: string;
  symbol: string;
  full_name: string;
  decimal_places: number;
  rate: number;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface CurrencyFilterOption {
  value: string;
  label: string;
  symbol?: string;
  code?: string;
  avatar_url?: string;
}
interface CurrenciesState extends FilteredState<Currency> {
  selectedCurrency: Currency | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: CurrencyFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: CurrenciesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'code',
  sortDirection: 'asc',
  searchTerm: null,
  selectedCurrency: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar currencies con paginación y filtros (para vistas de lista)
export const fetchCurrencies = createAsyncThunk<{ data: Currency[]; totalCount: number }, any>('currencies/fetchCurrencies', async (params: any) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, código, símbolo, nombre) - CACHEADO
export const fetchCurrencyFilterOptions = createAsyncThunk(
  'currencies/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const currenciesState = state.currencies as CurrenciesState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      currenciesState.filterOptionsLastFetch &&
      Date.now() - currenciesState.filterOptionsLastFetch < CACHE_DURATION &&
      currenciesState.filterOptions.length > 0
    ) {
      return currenciesState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/currencies?startRow=0&endRow=200&sortProperty=code&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const currencies = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: CurrencyFilterOption[] = currencies.map((currency: any) => ({
        value: currency.id.toString(),
        label: `${currency.code} - ${currency.full_name}`,
        symbol: currency.symbol,
        code: currency.code,
        avatar_url: currency.avatar_url
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchCurrencyById = createAsyncThunk('currencies/fetchCurrencyById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createCurrency = createAsyncThunk('currencies/createCurrency', async (currency: Partial<Currency>) => {
  throw new Error('Not implemented yet');
});
export const updateCurrency = createAsyncThunk('currencies/updateCurrency', async ({ id, data }: { id: number; data: Partial<Currency> }) => {
  throw new Error('Not implemented yet');
});
export const deleteCurrency = createAsyncThunk('currencies/deleteCurrency', async (id: number) => {
  throw new Error('Not implemented yet');
});
const currenciesSlice = createSlice({
  name: 'currencies',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectCurrency: (state, action: PayloadAction<Currency | null>) => {
      state.selectedCurrency = action.payload;
    },
    clearSelection: (state) => {
      state.selectedCurrency = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchCurrencies (lista completa)
    builder
      .addCase(fetchCurrencies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrencies.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchCurrencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar monedas';
      })
      // Casos para fetchCurrencyFilterOptions (opciones cacheadas)
      .addCase(fetchCurrencyFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchCurrencyFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchCurrencyFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectCurrencyFilterOptions = (state: any) => state.currencies.filterOptions;
export const selectCurrencyFilterOptionsLoading = (state: any) => state.currencies.filterOptionsLoading;
export const selectCurrencyFilterOptionsError = (state: any) => state.currencies.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areCurrencyFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.currencies.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectCurrency, clearSelection } = currenciesSlice.actions;
export default currenciesSlice.reducer;
